import * as Immutable from 'immutable'
import { Button } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DeleteIcon from '../../Common/Icons/DeleteIcon'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import { ClearImageSelectedImage, SelectAllImageSelectedImage } from '../../UIData/_actions/Account/AccountStoreActions'
import { type BatchImageSelection } from '../../UserData/_actions/UserImageActions'
import BatchSelectionActionBar from '../BatchSelectionActionBar'
import DeleteImageDialog from './DeleteImageDialog'

interface Props {
  listId: string
  search: string
}

const buildBatchSelection = (
  imageSelectedImages: Immutable.OrderedSet<number>,
  imageSelectedAllImages: boolean,
  imageExcludedSelectedImages: Immutable.OrderedSet<number>,
  search: string,
): BatchImageSelection => ({
  ids: imageSelectedImages.toArray(),
  selectAll: imageSelectedAllImages,
  excludeIds: imageExcludedSelectedImages.toArray(),
  filters: {
    search: search,
  },
})

export default function ImageListActions(props: Props) {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')

  const imageSelectedImages = useAppSelector(state => state.get('UIData').get('account').get('imageSelectedImages'))
  const imageSelectedAllImages = useAppSelector(state => state.get('UIData').get('account').get('imageSelectedAllImages'))
  const imageExcludedSelectedImages = useAppSelector(state => state.get('UIData').get('account').get('imageExcludedSelectedImages'))
  const totalMatchingImages = useAppSelector(state =>
    state.get('userData')
      .get('images')
      .get('itemLists')
      .get(props.listId)
      ?.get('total') ?? 0)

  const [deleteDialogOpened, setDeleteDialogOpened] = useState(false)

  const selectedCount = useMemo(() => {
    if (imageSelectedAllImages) {
      return Math.max(totalMatchingImages - imageExcludedSelectedImages.count(), 0)
    }

    return imageSelectedImages.count()
  }, [imageExcludedSelectedImages, imageSelectedAllImages, imageSelectedImages, totalMatchingImages])

  const remainingToSelect = useMemo(() => {
    return Math.max(totalMatchingImages - selectedCount, 0)
  }, [selectedCount, totalMatchingImages])

  const getBatchSelection = useCallback((): BatchImageSelection => (
    buildBatchSelection(
      imageSelectedImages,
      imageSelectedAllImages,
      imageExcludedSelectedImages,
      props.search,
    )
  ), [imageExcludedSelectedImages, imageSelectedAllImages, imageSelectedImages, props.search])

  const dialogSelection = useMemo(() => {
    if (!deleteDialogOpened) {
      return null
    }

    return getBatchSelection()
  }, [deleteDialogOpened, getBatchSelection])

  const onClear = useCallback(() => {
    dispatch(ClearImageSelectedImage())
  }, [dispatch])

  const onSelectAll = useCallback(() => {
    dispatch(SelectAllImageSelectedImage())
  }, [dispatch])

  const onDelete = useCallback(() => {
    setDeleteDialogOpened(true)
  }, [])

  return <>
    <BatchSelectionActionBar
      selectedCount={selectedCount}
      totalMatchingCount={totalMatchingImages}
      selectAllActive={imageSelectedAllImages}
      onClear={onClear}
      onSelectAll={remainingToSelect > 0 ? onSelectAll : undefined}
      actions={<Button
        color="error"
        startIcon={<DeleteIcon />}
        onClick={onDelete}
      >{t('Delete')}</Button>}
    />

    {deleteDialogOpened && dialogSelection ? <DeleteImageDialog
      opened={deleteDialogOpened}
      selection={dialogSelection}
      selectionCount={selectedCount}
      onClose={() => setDeleteDialogOpened(false)}
    /> : null}
  </>
}
